// Unsaved entry leave guard (#10280).
//
// Asks before the user leaves an SSR entry form holding data they typed but
// never saved. In-app exits (sidebar and other links, Exit, Close, New) and
// browser Back/Forward are held back behind the shared in-page modal in
// ssr/common/unsaved_entry_modal.html.
// Closing or reloading the tab can only use the browser's own prompt; browsers
// ignore page-supplied text there.
//
// An entry form is any page with a standard Save/Submit button; the dirty rule
// and form scope are the ones formdraft.js already uses for its backups.
import {
    SAVE_PATH_RE, hasSuccessFlash, standardSaveButtons, findFormScope, currentRecordId,
    isUserEdit, isEntryTarget,
} from './formdraft.js';
import { openModal, closeModal } from './modal.js';

const MODAL_NAME = 'unsaved-entry-leave';
// Saves that navigate away on success carry no success flash in their body.
const NAVIGATION_HEADERS = ['HX-Redirect', 'HX-Refresh', 'HX-Location'];

let entry = null; // {scope, key}
let unsaved = false;
let lastUserInteraction = 0;
let suppressUntil = 0;
let heldBack = null;
let heldHistory = false;
// Where the entry sat in browser history when it was last edited.
let entryHistory = null;
// The confirmed exit control, until its own request finishes.
let leavingFrom = null;

export function isSavedResponse(xhr) {
    if (!xhr || xhr.status < 200 || xhr.status >= 300) return false;
    if (hasSuccessFlash(xhr.responseText)) return true;
    return NAVIGATION_HEADERS.some((name) => Boolean(xhr.getResponseHeader(name)));
}

// The control whose click would take the user away from the entry, if any.
export function leaveTarget(evt, location = window.location) {
    if (evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return null;
    const target = evt.target;
    if (!target || typeof target.closest !== 'function') return null;
    const marked = target.closest('[data-leaves-form]');
    if (marked) return marked;
    const anchor = target.closest('a[href]');
    if (!anchor) return null;
    if (anchor.target && anchor.target !== '_self') return null;
    if (anchor.hasAttribute('download') || anchor.hasAttribute('hx-get') || anchor.hasAttribute('hx-post')) return null;
    let url;
    try {
        url = new URL(anchor.getAttribute('href'), location.href);
    } catch {
        return null;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    const samePage = url.origin === location.origin
        && url.pathname === location.pathname
        && url.search === location.search;
    return samePage ? null : anchor;
}

function hasUnsavedEntry() {
    return unsaved && Boolean(entry && entry.scope.isConnected);
}

function markEdited(trusted) {
    if (!entry) return;
    // Only programmatic post-save events are ignored; real typing is always counted.
    if (Date.now() <= suppressUntil && !trusted) return;
    if (!isUserEdit(trusted, lastUserInteraction)) return;
    unsaved = true;
    entryHistory = { url: window.location.href, state: window.history.state };
}

function hookGridEdits(scope) {
    scope.querySelectorAll('[data-ag-id]').forEach((el) => {
        const api = window[`aggridApi:${el.dataset.agId}`];
        if (!api || api.__unsavedEntryHooked || (api.isDestroyed && api.isDestroyed())) return;
        api.__unsavedEntryHooked = true;
        // AG-Grid tags its own edit paths (commit, paste, fill, clear) with a
        // source; plain setDataValue() calls from page code have none.
        api.addEventListener('cellValueChanged', (evt) => markEdited(Boolean(evt.source)));
    });
}

function scanForEntry() {
    const ctx = standardSaveButtons()[0];
    if (!ctx) {
        entry = null;
        unsaved = false;
        leavingFrom = null;
        return;
    }
    const scope = findFormScope(ctx.slug, ctx.btn);
    // A different record (or a freshly saved one) starts clean.
    const key = `${ctx.slug}:${currentRecordId(ctx.slug, scope)}`;
    if (!entry || entry.key !== key) {
        unsaved = false;
        leavingFrom = null;
    }
    entry = { scope, key };
    hookGridEdits(scope);
}

function hasLeaveModal() {
    // Pages not built on ssr/base.html lack the modal; never trap the user there.
    return Boolean(document.querySelector(`[data-modal="${MODAL_NAME}"]`));
}

function holdBack(target, viaHistory) {
    heldBack = target;
    heldHistory = viaHistory;
    openModal(MODAL_NAME);
    document.querySelector('[data-unsaved-entry-stay]')?.focus();
}

function leave() {
    const target = heldBack;
    const viaHistory = heldHistory;
    heldBack = null;
    heldHistory = false;
    closeModal(MODAL_NAME);
    unsaved = false;
    // The guard put the entry's URL back on top of the page the user was
    // heading to, so one step back reaches it for both Back and Forward.
    if (viaHistory) {
        window.history.back();
        return;
    }
    leavingFrom = target;
    const destination = target?.href
        ? new URL(target.href, window.location.href)
        : null;
    if (target?.matches?.('[data-search-result-link]') && destination) {
        const isPersistentRoute = window.smaacIsPersistentNavigationUrl?.(destination.href);
        if (isPersistentRoute && window.smaacPersistentNavigateTo) {
            Promise.resolve(window.smaacPersistentNavigateTo(destination.href)).then((result) => {
                // Persistent navigation can fail without leaving the current page.
                if (result === false && entry?.scope.isConnected) unsaved = true;
            });
            return;
        }
    }
    if (target && target.isConnected) target.click();
}

function stay() {
    const target = heldBack;
    heldBack = null;
    heldHistory = false;
    closeModal(MODAL_NAME);
    if (target && target.isConnected) target.focus();
}

function init() {
    let scanQueued = null;
    const queueScan = () => {
        clearTimeout(scanQueued);
        scanQueued = setTimeout(scanForEntry, 250);
    };
    scanForEntry();
    document.addEventListener('htmx:afterSettle', queueScan);

    ['pointerdown', 'keydown'].forEach((eventName) => {
        document.addEventListener(eventName, (evt) => {
            if (evt.isTrusted && entry && isEntryTarget(entry.scope, evt.target)) {
                lastUserInteraction = Date.now();
            }
        }, true);
    });
    ['input', 'change'].forEach((eventName) => {
        document.addEventListener(eventName, (evt) => {
            if (entry && isEntryTarget(entry.scope, evt.target)) markEdited(evt.isTrusted);
        }, true);
    });

    // The form is still on screen when the confirmed exit's request fails, so
    // keep guarding it. htmx fires afterRequest for errors, aborts and timeouts too.
    document.addEventListener('htmx:afterRequest', (evt) => {
        if (!leavingFrom || evt.detail?.elt !== leavingFrom) return;
        leavingFrom = null;
        if (!evt.detail.successful) unsaved = true;
    });

    // beforeOnLoad runs before htmx follows an HX-Redirect, so a save that
    // navigates away is already clean when beforeunload fires.
    document.addEventListener('htmx:beforeOnLoad', (evt) => {
        const path = evt.detail?.requestConfig?.path || '';
        if (!SAVE_PATH_RE.test(path) || !isSavedResponse(evt.detail?.xhr)) return;
        unsaved = false;
        // Post-save OOB swaps fire change events; don't let them re-dirty.
        suppressUntil = Date.now() + 2000;
    });

    // Window capture runs ahead of persistentnav.js's document capture handler,
    // htmx and the Exit button's own click handler.
    window.addEventListener('click', (evt) => {
        if (!hasUnsavedEntry()) return;
        const target = leaveTarget(evt);
        if (!target || !hasLeaveModal()) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();
        holdBack(target, false);
    }, true);

    document.addEventListener('click', (evt) => {
        if (evt.target.closest?.('[data-unsaved-entry-leave]')) leave();
        else if (evt.target.closest?.('[data-unsaved-entry-stay]')) stay();
    });
}

// Registered while FRAMEWORK_JS runs in <head>, ahead of the popstate handlers
// of htmx (history restore), aggrid.js (grid entry) and ssrcommon.js (reload),
// so a guarded Back/Forward reaches none of them. popstate cannot be cancelled:
// the URL has already moved, so push the entry's URL back while the user decides.
window.addEventListener('popstate', (evt) => {
    if (!hasUnsavedEntry() || !entryHistory || !hasLeaveModal()) return;
    const from = new URL(entryHistory.url);
    const to = window.location;
    // Hash-only moves stay on the entry.
    if (from.pathname === to.pathname && from.search === to.search) return;
    evt.stopImmediatePropagation();
    window.history.pushState(entryHistory.state, '', entryHistory.url);
    holdBack(null, true);
}, true);

// Registered while FRAMEWORK_JS runs in <head>, ahead of the deferred
// ssrpageloader.js, which skips its "Loading page" overlay for a cancelled unload.
window.addEventListener('beforeunload', (evt) => {
    if (!hasUnsavedEntry()) return;
    evt.preventDefault();
    // Older Chromium/Safari only prompt when returnValue is set.
    evt.returnValue = '';
});

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
